import React, { forwardRef, MouseEventHandler } from "react";
import {
  ButtonElement,
  ButtonText,
  LinkElement,
  SpinnerContainer,
} from "./Button.styles";
import { getGlobalTheme } from "../../app/global-theme";
import Spinner from "../Spinner";

export const ButtonSize = {
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
  xxl: "xxl",
} as const;

export type ButtonSize = (typeof ButtonSize)[keyof typeof ButtonSize];

export const ButtonAppearance = {
  primary: "primary",
  secondary: "secondary",
} as const;

export type ButtonAppearance =
  (typeof ButtonAppearance)[keyof typeof ButtonAppearance];

export interface ButtonProps {
  appearance?: ButtonAppearance;
  size?: ButtonSize;
  disabled?: boolean;
  href?: string;
  children: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  type?: "button" | "submit";
  onClick?:
    | MouseEventHandler<HTMLButtonElement>
    | (() => void)
    | (() => Promise<void>);
  style?: React.CSSProperties;
  outlined?: boolean;
  isLoading?: boolean;
  tabIndex?: number;
  mode?: "light" | "dark";
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      appearance = ButtonAppearance.primary,
      size = ButtonSize.md,
      href,
      children,
      disabled = false,
      onClick,
      prefix,
      suffix,
      type = "button",
      outlined = false,
      isLoading = false,
      tabIndex = 0,
      mode = "dark",
      ...props
    }: ButtonProps,
    ref
  ) => {
    const theme = getGlobalTheme(mode);
    const color = {
      [ButtonAppearance.primary]: theme.colors.button.primary.color,
      [ButtonAppearance.secondary]: theme.colors.button.secondary.color,
    };

    const bgColor = {
      [ButtonAppearance.primary]: theme.colors.button.primary.background,
      [ButtonAppearance.secondary]: theme.colors.button.secondary.background,
    };

    const bgHover = {
      [ButtonAppearance.primary]: theme.colors.button.primary.backgroundHover,
      [ButtonAppearance.secondary]:
        theme.colors.button.secondary.backgroundHover,
    };

    const spinnerColor = {
      [ButtonAppearance.primary]: theme.colors.button.primary.color,
      [ButtonAppearance.secondary]: theme.colors.button.secondary.color,
    };

    const padding = {
      [ButtonSize.sm]: "7px 11px 7px 11px",
      [ButtonSize.md]: "9px 14px 9px 14px",
      [ButtonSize.lg]: "9px 17px 9px 17px",
      [ButtonSize.xl]: "9px 17px 9px 17px",
      [ButtonSize.xxl]: "13px 25px 13px 25px",
    };

    const height = {
      [ButtonSize.sm]: "30px",
      [ButtonSize.md]: "38px",
      [ButtonSize.lg]: "38px",
      [ButtonSize.xl]: "42px",
      [ButtonSize.xxl]: "50px",
    };

    const textAppearance = {
      [ButtonSize.sm]: theme.fonts.sm.fontSize,
      [ButtonSize.md]: theme.fonts.medium.fontSize,
      [ButtonSize.lg]: theme.fonts.lg.fontSize,
      [ButtonSize.xl]: theme.fonts.xl.fontSize,
      [ButtonSize.xxl]: theme.fonts["2xl"].fontSize,
    };

    const borderRadius = {
      [ButtonSize.sm]: "4px",
      [ButtonSize.md]: "4px",
      [ButtonSize.lg]: "6px",
      [ButtonSize.xl]: "6px",
      [ButtonSize.xxl]: "6px",
    };

    const iconSpacing = {
      [ButtonSize.sm]: "8px",
      [ButtonSize.md]: "8px",
      [ButtonSize.lg]: "8px",
      [ButtonSize.xl]: "12px",
      [ButtonSize.xxl]: "12px",
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Tag = (href ? LinkElement : ButtonElement) as any;

    const getTag = () => (
      <Tag
        disabled={disabled}
        href={href}
        type={!href ? type : null}
        onClick={onClick}
        ref={ref}
        $bg={bgColor[appearance]}
        $bgHover={bgHover[appearance]}
        $padding={padding[size]}
        $borderRadius={borderRadius[size]}
        $outlined={outlined}
        $color={color[appearance]}
        $height={height[size]}
        tabIndex={tabIndex}
        {...props}
      >
        {!isLoading && (
          <>
            {prefix && (
              <span
                style={{
                  marginRight: iconSpacing[size],
                  display: "inline-flex",
                }}
              >
                {prefix}
              </span>
            )}
            <ButtonText size={textAppearance[size]} color={color[appearance]}>
              {children}
            </ButtonText>
            {suffix && (
              <span
                style={{
                  marginLeft: iconSpacing[size],
                  display: "inline-flex",
                }}
              >
                {suffix}
              </span>
            )}
          </>
        )}

        {isLoading && (
          <SpinnerContainer>
            <Spinner
              color={
                outlined
                  ? theme.colors.button.primary.background
                  : spinnerColor[appearance]
              }
            />
          </SpinnerContainer>
        )}
      </Tag>
    );

    return href ? <a href={href}>{getTag()}</a> : getTag();
  }
);

export default Button;
